import React, { useState, useEffect, useCallback } from 'react'
import { useDispatch } from 'react-redux'

import clsx from 'clsx'
import { makeStyles } from '@material-ui/styles'
import { Card, CardContent, TextField, Grid, Fab, Button, CircularProgress, Typography, Divider, FormControlLabel } from '@material-ui/core'

import Autocomplete from '@material-ui/lab/Autocomplete'
import Checkbox from '@material-ui/core/Checkbox'
import validate from 'validate.js'
import useRouter from 'utils/useRouter'
import { useSetorService } from 'hooks/useSetorService'
import { useTipoExameService } from 'hooks/useTipoExameService'
import InputAdornment from '@material-ui/core/InputAdornment'
import AddIcon from '@material-ui/icons/Add'
import Tooltip from '@material-ui/core/Tooltip'
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank'
import CheckBoxIcon from '@material-ui/icons/CheckBox'

import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogTitle from '@material-ui/core/DialogTitle'
import { BackButton } from 'components/BackButton/BackButton'

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />
const checkedIcon = <CheckBoxIcon fontSize="small" />

const useStyles = makeStyles((theme) => ({
  root: {},
  alert: {
    marginBottom: theme.spacing(3),
  },
  carregando: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: 20,
  },
  formGroup: {
    marginBottom: theme.spacing(3),
  },
  fieldGroup: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1),
    padding: theme.spacing(2),
    paddingTop: theme.spacing(3),
  },
  fieldHint: {
    margin: theme.spacing(1, 0),
  },
  tags: {
    marginTop: theme.spacing(1),
    '& > * + *': {
      marginLeft: theme.spacing(1),
    },
  },
  flexGrow: {
    flexGrow: 1,
  },
  dateField: {
    '& + &': {
      marginLeft: theme.spacing(2),
    },
  },
  modal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  paper: {
    width: '50%',
    backgroundColor: theme.palette.background.paper,
    border: '2px solid #000',
    boxShadow: theme.shadows[5],
    padding: theme.spacing(2, 4, 3),
  },
  enviarImagemComputador: {
    display: 'none',
  },
  fotos: {
    margin: theme.spacing(1),
  },
  actions: {
    marginTop: theme.spacing(3),
  },
  buttons: {
    marginTop: theme.spacing(2),
  },
}))

const schema = {
  nome: {
    presence: { allowEmpty: false, message: '^Cargo é obrigatório' },
  },
  setores: {
    presence: { allowEmpty: false, message: '^Cargo é obrigatório' },
  },
  cod: {
    presence: { allowEmpty: false, message: '^Código é obrigatório' },
  },
}

export const Formulario = (props) => {
  const { className, dados, editar, ...rest } = props
  const setorService = useSetorService()
  const tipoExameService = useTipoExameService()
  const [processando, setProcessando] = useState(false)
  const classes = useStyles()
  const [openAlertDelete, setOpenAlertDelete] = useState(false)
  const dispatch = useDispatch()
  const router = useRouter()
  const [isFormError, setIsFormError] = useState(false)
  const handleClickOpenAlertDelete = () => {
    setOpenAlertDelete(true)
  }

  const handleCloseAlertDelete = () => {
    setOpenAlertDelete(false)
  }

  const handleDelete = async (event) => {
    event.preventDefault()
    await tipoExameService.deletarTipoExame(dados.id)
    await tipoExameService.listarTiposExamesGlobal()
    router.history.push('/exames')
  }

  const [formState, setFormState] = useState({
    isValid: false,
    values: {
      agendado: null,
      objetosPericia: [],
      inativado: null,
      setorResponsavelNatal: null,
      setorResponsavelMossoro: null,
      setorResponsavelCaico: null,
      setorResponsavelPDF: null,
    },
    touched: {},
    errors: {},
  })

  const setQuesitos = (quesitos) => {
    quesitos.every(async (quesito, i) => {
      setFormState((formState) => ({
        ...formState,
        values: {
          ...formState.values,
          [`quesito${i}`]: quesito.quesito,
        },
      }))
    })
  }

  const handleChangeSetores = useCallback((newValue) => {
    setFormState((prevState) => ({
      ...prevState,
      values: {
        ...prevState.values,
        setores: newValue,
      },
    }))
  }, [])

  const handleChangeSetorResponsavel = useCallback((newValue, regional) => {
    setFormState((prevState) => ({
      ...prevState,
      values: {
        ...prevState.values,
        [`setorResponsavel${regional}`]: newValue,
      },
    }))
  }, [])

  const verificarSetorResponsavelEmRegional = (tiposExamesResponsaveis, regional) => {
    if (!tiposExamesResponsaveis?.length) {
      return null
    }
    let result = null
    tiposExamesResponsaveis.forEach((tipoExameResponsavel) => {
      if (tipoExameResponsavel.regional?.sigla === regional) {
        result = tipoExameResponsavel.setorResponsavel
      }
    })
    return result
  }

  useEffect(() => {
    setFormState((formState) => ({
      ...formState,
      values: {
        ...formState.values,
        nome: dados.nome,
        setores: dados.setResps,
        agendado: dados.agendado,
        cod: dados.cod,
        objetosPericia: dados.objetosPericia,
        inativado: dados.inativado,
        setorResponsavelNatal: verificarSetorResponsavelEmRegional(dados.responsaveisCadastro, 'ITEP-Natal'),
        setorResponsavelMossoro: verificarSetorResponsavelEmRegional(dados.responsaveisCadastro, 'ITEP-Mossoró'),
        setorResponsavelCaico: verificarSetorResponsavelEmRegional(dados.responsaveisCadastro, 'ITEP-PDF'),
        setorResponsavelPDF: verificarSetorResponsavelEmRegional(dados.responsaveisCadastro, 'ITEP-Caicó'),
      },
    }))
    if (dados.quesitos) {
      setQuesitos(dados.quesitos)
    }
  }, [dados])

  useEffect(() => {
    const errors = validate(formState.values, schema)

    setFormState((formState) => ({
      ...formState,
      isValid: !errors,
      errors: errors || {},
    }))
  }, [formState.values])

  useEffect(() => {
    if (!formState.values.nome || !formState.values.setores?.length || !formState.values.cod || !formState.values.objetosPericia?.length) {
      setIsFormError(true)
    } else {
      setIsFormError(false)
    }
  }, [formState.values])

  const handleChange = (event) => {
    event.persist()

    setFormState((formState) => ({
      ...formState,
      values: {
        ...formState.values,
        [event.target.name]: event.target.type === 'checkbox' ? event.target.checked : event.target.value,
      },
      touched: {
        ...formState.touched,
        [event.target.name]: true,
      },
    }))
  }

  /**
   * @description Obtem a lista de setores
   */
  const [setores, setSetores] = useState([])
  const fetchSetores = useCallback(async () => {
    const result = await setorService.listarSetoresAsync()
    setSetores(result)
  }, [dispatch, router])

  /**
   * @description useEffect para chamar as funções fetchDados
   */
  useEffect(() => {
    fetchSetores()
  }, [fetchSetores])

  const [arrayQuesitos, setArrayQuesitos] = useState(['quesito0'])
  const handleAdicionarQuesito = () => {
    const newInput = `quesito${arrayQuesitos.length}`
    const array = arrayQuesitos.concat([newInput])
    setArrayQuesitos(array)
  }

  useEffect(() => {
    if (editar && formState.isValid) {
      const array = []
      let i = 0
      Object.keys(formState.values).forEach((key) => {
        if (key.indexOf('quesito') !== -1) {
          array.push(`quesito${i}`)
          i += 1
        }
      })
      if (array.length) {
        setArrayQuesitos(array)
      }
    }
  }, [formState.isValid, formState.values, editar])

  const handleChangeObjetoPericiando = async (event, newValue) => {
    try {
      setFormState((prevState) => ({
        ...prevState,
        values: {
          ...prevState.values,
          objetosPericia: newValue,
        },
      }))
    } catch (err) {
      throw err
    }
  }

  const handleSubmit = async (event) => {
    event.preventDefault()
    let exame = null

    const ques = []
    if (formState.isValid) {
      setProcessando(true)
      Object.keys(formState.values).forEach((key) => {
        if (key.indexOf('quesito') !== -1) {
          ques.push(formState.values[key])
        }
      })
      exame = {
        values: {
          nome: formState.values.nome,
          setores: formState.values.setores?.map((setor) => setor.id) || null,
          quesitos: ques,
          agendado: formState.values.agendado,
          cod: formState.values.cod,
          objetosPericia: formState.values.objetosPericia,
          inativado: formState.values.inativado,
          setorResponsavelNatal: formState.values.setorResponsavelNatal,
          setorResponsavelMossoro: formState.values.setorResponsavelMossoro,
          setorResponsavelCaico: formState.values.setorResponsavelCaico,
          setorResponsavelPDF: formState.values.setorResponsavelPDF,
        },
      }
      if (editar) {
        await tipoExameService.editarTipoExame(dados.id, exame.values)
      } else {
        await tipoExameService.cadastrarTipoExame(exame.values)
      }
      router.history.push('/exames')
    }
  }

  const hasError = (field) => formState.touched[field] && formState.errors[field]

 

  return (
    <Card {...rest} className={clsx(classes.root, className)}>
      {editar && !dados.quesitos ? (
        <div className={classes.carregando}>
          <CircularProgress disableShrink />
        </div>
      ) : (
        <CardContent>
          <Grid alignItems="center" container justifyContent="space-between">
            <Grid item md={12} sm={12} xs={12}>
              <Typography>
                <b>DADOS BÁSICOS</b>
              </Typography>
              <Divider style={{ marginTop: '2px' }} />
            </Grid>
            <Grid item container spacing={2} justifyContent="space-between" className={classes.fieldGroup}>
              <Grid className={classes.item} item md={6} sm={6} xs={12}>
                <TextField
                  error={hasError('nome')}
                  fullWidth
                  variant="outlined"
                  helperText={hasError('nome') ? formState.errors.nome[0] : null}
                  label="NOME DO EXAME"
                  name="nome"
                  onChange={handleChange}
                  value={formState.values.nome || ''}
                  required
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>

              <Grid className={classes.item} item md={12} sm={12} xs={12}>
                {editar ? null : (
                  <Autocomplete
                    multiple
                    name="setores"
                    noOptionsText="Sem informações"
                    options={setores}
                    disableCloseOnSelect
                    getOptionSelected={(option, value) => option.id === value.id}
                    onChange={(event, newValue) => {
                      handleChangeSetores(newValue)
                    }}
                    getOptionLabel={(option) => `${option.nome} / ${option.divisao.sigla} / ${option.divisao.regional.sigla} / ${option.divisao.regional.orgao.sigla}`}
                    renderOption={(option, { selected }) => (
                      <>
                        <Checkbox
                          icon={icon}
                          checkedIcon={checkedIcon}
                          style={{
                            marginRight: 8,
                          }}
                          checked={selected}
                        />
                        {`${option.nome} / ${option.divisao.sigla} / ${option.divisao.regional.sigla} / ${option.divisao.regional.orgao.sigla}`}
                      </>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        label="SETORES QUE REALIZAM O EXAME"
                        InputLabelProps={{
                          shrink: true,
                        }}
                      />
                    )}
                    required
                  />
                )}
                {editar && formState.values.setores ? (
                  <Autocomplete
                    value={formState.values.setores}
                    multiple
                    name="setores"
                    noOptionsText="Sem informações"
                    options={setores}
                    disableCloseOnSelect
                    getOptionSelected={(option, value) => option.id === value.id}
                    onChange={(event, newValue) => {
                      handleChangeSetores(newValue)
                    }}
                    getOptionLabel={(option) => `${option.nome} / ${option.divisao.sigla} / ${option.divisao.regional.sigla} / ${option.divisao.regional.orgao.sigla}`}
                    renderOption={(option, { selected }) => (
                      <>
                        <Checkbox
                          icon={icon}
                          checkedIcon={checkedIcon}
                          style={{
                            marginRight: 8,
                          }}
                          checked={selected}
                        />
                        {`${option.nome} / ${option.divisao.sigla} / ${option.divisao.regional.sigla} / ${option.divisao.regional.orgao.sigla}`}
                      </>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        label="SETORES QUE REALIZAM O EXAME"
                        InputLabelProps={{
                          shrink: true,
                        }}
                      />
                    )}
                    required
                  />
                ) : null}
              </Grid>

              <Grid className={classes.item} item md={6} sm={6} xs={12}>
                <TextField
                  error={hasError('cod')}
                  fullWidth
                  variant="outlined"
                  helperText={hasError('cod') ? formState.errors.cod[0] : null}
                  label="CÓDIGO"
                  name="cod"
                  onChange={handleChange}
                  value={formState.values.cod || ''}
                  required
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>

              <Grid className={classes.item} item md={6} sm={6} xs={12}>
                <Autocomplete
                  value={formState.values.objetosPericia || []}
                  multiple
                  name="objetosPericia"
                  noOptionsText="Sem informações"
                  options={['PERICIANDO', 'CADAVER', 'VEICULO', 'LOCAL', 'VESTIGIO', 'N/A']}
                  disableCloseOnSelect
                  onChange={(event, newValue) => {
                    handleChangeObjetoPericiando(event, newValue)
                  }}
                  getOptionLabel={(option) => option}
                  renderOption={(option, { selected }) => (
                    <>
                      <Checkbox
                        icon={icon}
                        checkedIcon={checkedIcon}
                        style={{
                          marginRight: 8,
                        }}
                        checked={selected}
                      />
                      {`${option}`}
                    </>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      label="OBJETOS DE PERÍCIA"
                      InputLabelProps={{
                        shrink: true,
                      }}
                      required="true"
                    />
                  )}
                  required
                />
              </Grid>

              <Grid item md={6} sm={6} xs={12} className={classes.field}>
                <FormControlLabel control={<Checkbox checked={formState.values.inativado || null} onChange={handleChange} name="inativado" />} label="Inativado" />
              </Grid>
              <Grid item md={6} sm={6} xs={12} className={classes.field}>
                <FormControlLabel control={<Checkbox checked={formState.values.agendado || null} onChange={handleChange} name="agendado" />} label="As solicitações devem ser agendadas" />
              </Grid>

              <Grid item md={12} sm={12} xs={12}>
                <Typography>Responsavel pelo cadastro em Natal-RN</Typography>
                <Autocomplete
                  style={{ marginTop: '10px' }}
                  name="setorResposanvelNatal"
                  value={formState.values.setorResponsavelNatal}
                  noOptionsText="Sem informações"
                  options={setores}
                  disableCloseOnSelect
                  getOptionSelected={(option, value) => option.id === value.id}
                  onChange={(event, newValue) => {
                    handleChangeSetorResponsavel(newValue, 'Natal')
                  }}
                  getOptionLabel={(option) => `${option.nome} / ${option.divisao.sigla} / ${option.divisao.regional.sigla}`}
                  renderOption={(option, { selected }) => (
                    <>
                      <Checkbox
                        icon={icon}
                        checkedIcon={checkedIcon}
                        style={{
                          marginRight: 8,
                        }}
                        checked={selected}
                      />
                      {`${option.nome} / ${option.divisao.sigla} / ${option.divisao.regional.sigla}`}
                    </>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      label="Setor Responsável"
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  )}
                  required
                />
              </Grid>
              <Grid item md={12} sm={12} xs={12}>
                <Typography>Responsavel pelo cadastro em Mossoró-RN</Typography>
                <Autocomplete
                  style={{ marginTop: '10px' }}
                  name="setorResposanvelMossoro"
                  noOptionsText="Sem informações"
                  options={setores}
                  value={formState.values.setorResponsavelMossoro}
                  disableCloseOnSelect
                  getOptionSelected={(option, value) => option.id === value.id}
                  onChange={(event, newValue) => {
                    handleChangeSetorResponsavel(newValue, 'Mossoro')
                  }}
                  getOptionLabel={(option) => `${option.nome} / ${option.divisao.sigla} / ${option.divisao.regional.sigla}`}
                  renderOption={(option, { selected }) => (
                    <>
                      <Checkbox
                        icon={icon}
                        checkedIcon={checkedIcon}
                        style={{
                          marginRight: 8,
                        }}
                        checked={selected}
                      />
                      {`${option.nome} / ${option.divisao.sigla} / ${option.divisao.regional.sigla}`}
                    </>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      label="Setor Responsável"
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  )}
                  required
                />
              </Grid>
              <Grid item md={12} sm={12} xs={12}>
                <Typography>Responsavel pelo cadastro em Caicó-RN</Typography>
                <Autocomplete
                  style={{ marginTop: '10px' }}
                  name="setorResposanvelCaico"
                  noOptionsText="Sem informações"
                  options={setores}
                  value={formState.values.setorResponsavelCaico}
                  disableCloseOnSelect
                  getOptionSelected={(option, value) => option.id === value.id}
                  onChange={(event, newValue) => {
                    handleChangeSetorResponsavel(newValue, 'Caico')
                  }}
                  getOptionLabel={(option) => `${option.nome} / ${option.divisao.sigla} / ${option.divisao.regional.sigla}`}
                  renderOption={(option, { selected }) => (
                    <>
                      <Checkbox
                        icon={icon}
                        checkedIcon={checkedIcon}
                        style={{
                          marginRight: 8,
                        }}
                        checked={selected}
                      />
                      {`${option.nome} / ${option.divisao.sigla} / ${option.divisao.regional.sigla}`}
                    </>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      label="Setor Responsável"
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  )}
                  required
                />
              </Grid>
              <Grid item md={12} sm={12} xs={12}>
                <Typography>Responsavel pelo cadastro em Pau dos ferros-RN</Typography>
                <Autocomplete
                  style={{ marginTop: '10px' }}
                  name="setorResposanvelPDF"
                  noOptionsText="Sem informações"
                  options={setores}
                  value={formState.values.setorResponsavelPDF}
                  disableCloseOnSelect
                  getOptionSelected={(option, value) => option.id === value.id}
                  onChange={(event, newValue) => {
                    handleChangeSetorResponsavel(newValue, 'PDF')
                  }}
                  getOptionLabel={(option) => `${option.nome} / ${option.divisao.sigla} / ${option.divisao.regional.sigla}`}
                  renderOption={(option, { selected }) => (
                    <>
                      <Checkbox
                        icon={icon}
                        checkedIcon={checkedIcon}
                        style={{
                          marginRight: 8,
                        }}
                        checked={selected}
                      />
                      {`${option.nome} / ${option.divisao.sigla} / ${option.divisao.regional.sigla}`}
                    </>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      label="Setor Responsável"
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  )}
                  required
                />
              </Grid>

              <Grid className={classes.fieldGroup} alignItems="center" container item justifyContent="space-between">
                <Grid className={classes.item} item md={12} sm={12} xs={12}>
                  {arrayQuesitos.map((input, index) => (
                    <TextField
                      error={hasError('input')}
                      fullWidth
                      helperText={hasError('input') ? formState.errors.input[0] : null}
                      label="QUESITO"
                      multiline
                      style={{ marginTop: '10px' }}
                      name={input}
                      onChange={handleChange}
                      variant="outlined"
                      value={formState.values[input] || ''}
                      InputProps={{
                        endAdornment: index + 1 === arrayQuesitos.length && (
                          <InputAdornment position="start">
                            <Tooltip title="Adicionar quesito">
                              <Fab color="primary" size="small" onClick={() => handleAdicionarQuesito()}>
                                <AddIcon />
                              </Fab>
                            </Tooltip>
                          </InputAdornment>
                        ),
                      }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  ))}
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          <Grid container justifyContent="space-between">
            <Grid item>
              <BackButton size="small" />
            </Grid>
            <Grid item>
              {!editar ? (
                <Grid item>
                  <Button form="form-exame" color="secondary" variant="contained" disabled={processando || isFormError} onClick={handleSubmit}>
                    Cadastrar exame
                    {processando && <CircularProgress size={24} className={classes.buttonProgress} />}
                  </Button>
                </Grid>
              ) : (
                <div>
                  <Grid item container spacing={2}>
                    <Grid item>
                      <Button onClick={handleClickOpenAlertDelete} variant="contained" className={classes.buttons}>
                        Excluir
                      </Button>
                    </Grid>
                    <Grid item>
                      <Button form="form-exame" type="submit" color="secondary" variant="contained" disabled={processando || isFormError} className={classes.buttons} onClick={handleSubmit}>
                        {processando && <CircularProgress size={24} className={classes.buttonProgress} />}
                        Atualizar
                      </Button>
                    </Grid>
                  </Grid>
                </div>
              )}
            </Grid>
          </Grid>

          <Dialog open={openAlertDelete} onClose={handleCloseAlertDelete} aria-labelledby="alert-dialog-title" aria-describedby="alert-dialog-description">
            <DialogTitle id="alert-dialog-title">Tem certeza que deseja deletar?</DialogTitle>
            <DialogActions>
              <Button onClick={handleCloseAlertDelete} color="primary">
                Cancelar
              </Button>
              <Button onClick={handleDelete} className={clsx(classes.deleteButton)} autoFocus>
                Confirmar
              </Button>
            </DialogActions>
          </Dialog>
        </CardContent>
      )}
    </Card>
  )
}
